<template>
    <page :tabs="tabs">
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :rowData="couponPackageList"
            :isAutoWidth="isMobile"
            :isSearch="true"
            :suppressColumnVirtualisation="false"     
            @on-grid-ready="onGridReady"
        >
            <responsive-button
                :icon="['fas', 'plus']"
                variant="primary"
                @click="openAddCouponPackageModal"
            >Add Coupon Package</responsive-button>
        </grid>
        <coupon-package-form-modal
            :modal-id="couponPackageEditModalID"
            :is-create="false"
            :initial-values="currentCouponPackage ? currentCouponPackage : {}"
            :update="updateCouponPackage"
            @success-save="fetchCouponPackages"
        />
        <coupon-package-form-modal
            :modal-id="couponPackageCreateModalID"
            :is-create="true"
            :create="createCouponPackage"
            @success-save="fetchCouponPackages"
        />
        <modal
            :id="deleteCouponModalID"
            title="Delete Coupon"
            @ok="deleteCouponPackage"
        >Are you sure you want to delete this coupon?</modal>
    </page>
</template>

<script>
import API from '../../../api';
import { extractHTMLContent } from '../../../utils/strings';
import Grid from '../../generic/grid/grid.vue';
import Page from '../../generic/page/page.vue';
import Modal from '../../generic/modal/modal.vue';
import FormSelect from '../../generic/form-select/form-select.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue'
import ActionsCellRenderer from './actions-cell-renderer.vue';
import CouponPackageFormModal from './coupon-package-form-modal.vue';
import {
    ADMIN_COUPON_PKG_LIST_ENDPOINT,
    ADMIN_COUPON_PKG_BY_ID_ENDPOINT,
} from '../../../endpoints';
import { isMediumScreen } from '../../../utils/screens';

export default {
    name: 'CouponPackageList',
    components: {
        Page,
        Grid,
        Modal,
        CouponPackageFormModal,
        ResponsiveButton,
        FormSelect,
        actionsCellRenderer: ActionsCellRenderer,
    },
    data() {
        return {
            busy: false,
            gridApi: null,
            tabs: [
                {
                    title: 'Coupon Packages',
                    active: true,
                    route: '/app/admin/coupon-packages/',
                },
            ],
            currentPage: 0,
            couponPackageList: [],
            currentCouponPackage: null,
            currentCouponPackageID: null,
            couponPackageEditModalID: 'coupon-package-edit-modal',
            couponPackageCreateModalID: 'coupon-package-create-modal',
            deleteCouponModalID: 'coupon-package-delete-modal',
            statusValues: {
                'DISABLED': 'Disabled',
                'ACTIVE': 'Active',
                'INACTIVE': 'Inactive',
            },
            typeValues: {
                'FLAT_DISCOUNT': 'Fixed amount discount',
                'TRIAL_EXTENSION': 'Trial Extension',
                'PERCENT_DISCOUNT': 'Percentage Discount',
            },
        }
    },
    computed: {
        columnDefs() {
            return [
                {
                    headerName: 'Code',
                    field: 'name',
                    minWidth: 180,
                    maxWidth: 180,
                },
                {
                    headerName: 'Description',
                    field: 'description',
                    minWidth: 300,
                    autoHeight: true,
                    wrapText: true,
                },
                {
                    headerName: 'Period',
                    field: 'period',
                    minWidth: 100,
                    maxWidth: 100,
                    valueGetter: (params) => `${params.data.period} days`
                },
                {
                    headerName: 'Valid From',
                    field: 'date_from',
                    type: 'dateColumn',
                    minWidth: 180,
                },
                {
                    headerName: 'Valid Until',
                    field: 'date_to',
                    type: 'dateColumn',
                    minWidth: 180,
                },
                {
                    headerName: 'Status',
                    field: 'package_status',
                    valueGetter: (params) => this.statusValues[params.data.package_status],
                    minWidth: 100,
                    maxWidth: 100,
                },
                {
                    headerName: 'Type',
                    field: 'type',
                    valueGetter: (params) => this.typeValues[params.data.type],
                    minWidth: 180,
                    maxWidth: 180,
                },
                {
                    headerName: 'Discount',
                    field: 'discount',
                    minWidth: 100,
                    maxWidth: 100,
                },
                {
                    headerName: '',
                    pinned: this.isMobile ? 'right' : '',
                    maxWidth: 130,
                    minWidth: 130,
                    sortable: false,
                    resizable: false,
                    autoHeight: true,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        update: (params) => {
                            this.currentCouponPackage = params;
                            this.currentCouponPackageID = params.id
                            this.$bvModal.show(this.couponPackageEditModalID);
                        },
                        delete: (params) => {
                            this.currentCouponPackage = params;
                            this.currentCouponPackageID = params.id
                            this.$bvModal.show(this.deleteCouponModalID)
                        }
                    }
                },
            ]
        },
        isMobile() {
            return !this.isMediumScreen();
        }
    },
    mounted() {
        this.fetchCouponPackages()
    },
    methods: {
        isMediumScreen,
        onGridReady(params) {
            this.gridApi = params.api;
            if (!this.isMobile) {
                this.gridApi.sizeColumnsToFit();
            }
        },
        onPageLimitChanged() {
            this.fetchCouponPackages()
        },
        async fetchCouponPackages() {
            try {
                this.busy = true;
                const { data } = await API.get(ADMIN_COUPON_PKG_LIST_ENDPOINT());
                this.couponPackageList = data.map(item => {
                    return {
                        ...item,
                        description: extractHTMLContent(item.description)
                    }
                })
            } catch (e) {
                this.errorToast({ text: 'Could not load statuses' });
            } finally {
                this.busy = false;
            }
        },
        createCouponPackage(payload) {
            return API.post(ADMIN_COUPON_PKG_LIST_ENDPOINT(), payload);
        },
        updateCouponPackage(payload) {
            return API.patch(ADMIN_COUPON_PKG_BY_ID_ENDPOINT(this.currentCouponPackageID), payload);
        },
        async deleteCouponPackage() {
            await API.delete(ADMIN_COUPON_PKG_BY_ID_ENDPOINT(this.currentCouponPackageID));
            this.fetchCouponPackages()
        },
        openAddCouponPackageModal() {
            this.$bvModal.show(this.couponPackageCreateModalID);
        },
    }
}
</script>

<style lang="scss" scoped>

</style> 